import { useState, useEffect } from "react";
import { Layout } from "@/pages/Dashboard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Sparkles, Instagram, Facebook, Loader2, Copy, Check, RefreshCw, Image, Users, Search, ExternalLink, Send, CalendarDays, Hash, Plus, ChevronDown, Building2 } from "lucide-react";
import { apiClient, useAuth } from "@/App";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Link, useLocation } from "react-router-dom";
import { UpgradePrompt } from "@/components/Subscription";
import { copyToClipboard } from "@/lib/clipboard";
import { summariseMusicianTags, venueHandleList } from "@/lib/socialTags";
import { Kicker } from "@/design-system/primitives";
import { tokens } from "@/design-system/tokens";

// X (Twitter) Logo SVG Component
const XLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const SocialGenerator = () => {
  const { user, checkAuth } = useAuth();
  const location = useLocation();
  const [gigs, setGigs] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGig, setSelectedGig] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const [tone, setTone] = useState("professional");
  const [platforms, setPlatforms] = useState(["instagram", "facebook", "twitter"]);
  const [includeCollaborators, setIncludeCollaborators] = useState(true);
  const [includeVenue, setIncludeVenue] = useState(true);
  // The selected gig's venue record, for its saved social handles. null = not loaded / no venue.
  const [venueRecord, setVenueRecord] = useState(null);
  const [generatedPosts, setGeneratedPosts] = useState(null);
  const [copiedPlatform, setCopiedPlatform] = useState(null);
  
  // Image search state
  const [searchingImages, setSearchingImages] = useState(false);
  const [imageQuery, setImageQuery] = useState("");
  const [searchedImages, setSearchedImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  
  // Connected platforms state
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  
  // Hashtag suggestions
  const [suggestedHashtags, setSuggestedHashtags] = useState({});
  const [loadingHashtags, setLoadingHashtags] = useState(false);

  // Look up the selected gig's venue so we can say, truthfully, what will be tagged.
  useEffect(() => {
    const gig = gigs.find((g) => g.gig_id === selectedGig);
    setVenueRecord(null);
    if (!gig?.venue_id) return undefined;
    let cancelled = false;
    apiClient.get(`/venues/${encodeURIComponent(gig.venue_id)}`)
      .then((res) => { if (!cancelled) setVenueRecord(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedGig, gigs]);

  useEffect(() => {
    fetchGigs();
    fetchCollaborators();
    fetchConnectedPlatforms();
  }, []);

  // Pre-select a gig from the URL query (?gig_id=...) — used when the user
  // taps a reminder in the dashboard Social Schedule. Waits until the gig list
  // has loaded so we only select gigs the user actually owns.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qGigId = params.get("gig_id");
    if (!qGigId || gigs.length === 0) return;
    if (gigs.some(g => g.gig_id === qGigId)) {
      setSelectedGig(qGigId);
    }
  }, [gigs, location.search]);

  const fetchGigs = async () => {
    try {
      // Only fetch upcoming gigs for social posts
      const response = await apiClient.get("/gigs?time_filter=upcoming");
      // Sort chronologically (earliest first)
      const sorted = [...response.data].sort((a, b) => new Date(a.date) - new Date(b.date));
      setGigs(sorted);
    } catch (error) {
      toast.error("Failed to fetch gigs");
    } finally {
      setLoading(false);
    }
  };

  const fetchCollaborators = async () => {
    try {
      const response = await apiClient.get("/collaborators");
      setCollaborators(response.data);
    } catch (error) {
      console.error("Failed to fetch collaborators");
    }
  };

  const fetchConnectedPlatforms = async () => {
    try {
      const response = await apiClient.get("/social/credentials");
      setConnectedPlatforms(response.data.platforms || []);
    } catch (error) {
      console.error("Failed to fetch connected platforms");
    }
  };

  const handleGenerate = async () => {
    if (!selectedGig) {
      toast.error("Please select a gig first");
      return;
    }

    setGenerating(true);
    setGeneratedPosts(null);
    setSuggestedHashtags({});

    try {
      const response = await apiClient.post("/gigs/social-posts", {
        gig_id: selectedGig,
        platforms,
        tone,
        include_collaborators: includeCollaborators,
        include_venue: includeVenue
      });

      if (response.data.success) {
        setGeneratedPosts(response.data);
        
        // Auto-generate image search query from gig
        const gig = gigs.find(g => g.gig_id === selectedGig);
        if (gig) {
          setImageQuery(`${gig.genre} music concert live`);
          // Fetch hashtags for each platform
          fetchHashtags(gig);
        }
        
        toast.success("Posts generated successfully!");
      } else {
        throw new Error("Generation failed");
      }
    } catch (error) {
      console.error("Generation error:", error);
      toast.error("Failed to generate posts. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const fetchHashtags = async (gig) => {
    setLoadingHashtags(true);
    try {
      const results = {};
      for (const platform of platforms) {
        const res = await apiClient.post("/social/suggest-hashtags", {
          genre: gig.genre || "jazz",
          city: gig.city || "",
          venue: gig.venue_name || "",
          platform
        });
        results[platform] = res.data.hashtags || [];
      }
      setSuggestedHashtags(results);
    } catch (error) {
      console.error("Failed to fetch hashtags:", error);
    } finally {
      setLoadingHashtags(false);
    }
  };

  const addHashtagToPost = (platform, hashtag) => {
    if (!generatedPosts?.posts?.[platform]) return;
    const currentText = generatedPosts.posts[platform].text;
    if (currentText.includes(hashtag)) return; // Already added
    setGeneratedPosts(prev => ({
      ...prev,
      posts: {
        ...prev.posts,
        [platform]: {
          ...prev.posts[platform],
          text: currentText + " " + hashtag
        }
      }
    }));
    toast.success(`Added ${hashtag}`);
  };

  const handleSearchImages = async () => {
    if (!imageQuery.trim()) {
      toast.error("Please enter a search query");
      return;
    }

    setSearchingImages(true);
    try {
      const response = await apiClient.get(`/images/search?query=${encodeURIComponent(imageQuery)}&count=6`);
      setSearchedImages(response.data.images || []);
      if (response.data.images?.length === 0) {
        toast.info("No images found. Try a different search.");
      }
    } catch (error) {
      toast.error("Image search failed");
    } finally {
      setSearchingImages(false);
    }
  };

  const handleCopy = async (platform, text) => {
    try {
      await copyToClipboard(text);
      setCopiedPlatform(platform);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedPlatform(null), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const handleDirectPost = async (platform, text) => {
    if (!connectedPlatforms.includes(platform)) {
      toast.error(`${platform} is not connected. Connect your account first.`);
      return;
    }

    try {
      const response = await apiClient.post("/social/post", {
        gig_id: selectedGig,
        platform,
        post_text: text,
        image_url: selectedImage?.url
      });

      if (response.data.success) {
        toast.success(`Posted to ${platform}!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to post to ${platform}`);
    }
  };

  const togglePlatform = (platform) => {
    if (platforms.includes(platform)) {
      if (platforms.length > 1) {
        setPlatforms(platforms.filter(p => p !== platform));
      }
    } else {
      setPlatforms([...platforms, platform]);
    }
  };

  const selectedGigData = gigs.find(g => g.gig_id === selectedGig);
  
  // Detect if user is on mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'instagram': return <Instagram className="h-5 w-5" />;
      case 'facebook': return <Facebook className="h-5 w-5" />;
      case 'twitter': return <XLogo className="h-4 w-4" />;
      default: return null;
    }
  };
  
  const getPlatformName = (platform) => {
    switch (platform) {
      case 'instagram': return 'Instagram';
      case 'facebook': return 'Facebook';
      case 'twitter': return 'X';
      default: return platform;
    }
  };

  // Check subscription — check both top-level fields and nested subscription object
  const isPro = user?.plan_type === "pro" || user?.lifetime_access === true || user?.subscription?.is_pro === true;

  // Brief loading state while subscription data is being loaded
  if (user && !user.subscription && !user.plan_type) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!isPro) {
    return (
      <Layout>
        <UpgradePrompt feature="Social post generator" onUpgraded={checkAuth} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="social-page">
        {/* Header — DS tokens, matches the rest of the app's page intros */}
        <div style={{ marginBottom: tokens.space.xxl }}>
          <Kicker color={tokens.color.orange} style={{ marginBottom: tokens.space.xs }}>
            Social Media
          </Kicker>
          <div
            className="flex flex-row items-start justify-between"
            style={{ gap: tokens.space.md }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <h1
                style={{
                  margin: 0,
                  ...tokens.type.heroSm,
                  color: tokens.color.text,
                  textWrap: "balance",
                }}
              >
                Generate Promo Posts
              </h1>
              {/* Description: always visible on desktop; collapsible on mobile */}
              <p
                className={descExpanded ? "" : "hidden md:block"}
                style={{
                  margin: 0,
                  marginTop: tokens.space.sm,
                  ...tokens.type.body,
                  color: tokens.color.textMuted,
                }}
              >
                Create rich promotional posts with collaborator mentions and images.
              </p>
              <button
                type="button"
                className="md:hidden"
                onClick={() => setDescExpanded(v => !v)}
                aria-expanded={descExpanded}
                style={{
                  marginTop: 6,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  fontSize: 12,
                  color: tokens.color.textMuted,
                  transition: `color ${tokens.motion.fast}`,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = tokens.color.text)}
                onMouseLeave={(e) => (e.currentTarget.style.color = tokens.color.textMuted)}
              >
                <ChevronDown
                  className="h-3 w-3"
                  style={{
                    transition: "transform 180ms ease",
                    transform: descExpanded ? "rotate(180deg)" : "rotate(0)",
                  }}
                />
                {descExpanded ? "Less" : "About"}
              </button>
            </div>
            <Link to="/social/batch" style={{ flexShrink: 0, textDecoration: "none" }}>
              <button
                type="button"
                data-testid="batch-posts-btn"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: tokens.space.sm,
                  padding: "10px 14px",
                  background: "transparent",
                  color: tokens.color.textDim,
                  border: tokens.border.card,
                  borderRadius: tokens.radius.md + 1,
                  fontFamily: tokens.font.body,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                <CalendarDays className="h-4 w-4" />
                <span className="hidden sm:inline">Tour / Batch Posts</span>
              </button>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Configuration */}
          <div className="space-y-6">
            {/* Gig Selection */}
            <Card style={{ background: tokens.color.cardBg, border: tokens.border.card, borderRadius: tokens.radius.lg }}>
              <CardHeader>
                <CardTitle className="text-lg">Select a Gig</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="h-32 bg-secondary/50 rounded-lg animate-pulse" />
                ) : gigs.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No gigs found. Add some gigs first!
                  </p>
                ) : (
                  <ScrollArea className="h-[200px] pr-4">
                    <div className="space-y-2">
                      {gigs.map(gig => (
                        <div
                          key={gig.gig_id}
                          className={`p-4 rounded-lg cursor-pointer transition-all ${
                            selectedGig === gig.gig_id 
                              ? 'bg-primary/20 border border-primary' 
                              : 'bg-secondary/30 hover:bg-secondary/50 border border-transparent'
                          }`}
                          onClick={() => {
                            setSelectedGig(gig.gig_id);
                            setGeneratedPosts(null);
                            setSelectedImage(null);
                          }}
                          data-testid={`social-gig-${gig.gig_id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{gig.artist_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {gig.venue_name}, {gig.city}
                              </p>
                              <p className="text-xs text-muted-foreground font-mono mt-1">
                                {gig.date ? format(parseISO(gig.date), 'MMM d, yyyy') : 'No date'}
                                {gig.start_time && ` at ${gig.start_time}`}
                              </p>
                            </div>
                            {selectedGig === gig.gig_id && (
                              <Check className="h-5 w-5 text-primary" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>

            {/* Generation Options */}
            <Card style={{ background: tokens.color.cardBg, border: tokens.border.card, borderRadius: tokens.radius.lg }}>
              <CardHeader>
                <CardTitle className="text-lg">Post Options</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Platforms */}
                <div className="space-y-2">
                  <Label>Platforms</Label>
                  <div className="flex gap-2">
                    {['instagram', 'facebook', 'twitter'].map(platform => (
                      <Button
                        key={platform}
                        variant={platforms.includes(platform) ? "default" : "outline"}
                        size="sm"
                        onClick={() => togglePlatform(platform)}
                        className={platforms.includes(platform) ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-card text-foreground border-border hover:bg-muted'}
                        data-testid={`platform-${platform}`}
                      >
                        {getPlatformIcon(platform)}
                        <span className="ml-2">{getPlatformName(platform)}</span>
                        {connectedPlatforms.includes(platform) && (
                          <Check className="h-3 w-3 ml-1 text-green-400" />
                        )}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Tone */}
                <div className="space-y-2">
                  <Label>Tone</Label>
                  <Select value={tone} onValueChange={setTone}>
                    <SelectTrigger data-testid="tone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="casual">Casual & Friendly</SelectItem>
                      <SelectItem value="excited">Excited & Hype</SelectItem>
                      <SelectItem value="my_style">✨ My Style (uses your examples)</SelectItem>
                    </SelectContent>
                  </Select>
                  {tone === "my_style" && (
                    <p className="text-xs text-muted-foreground">
                      AI will match your voice. Add example posts in <Link to="/settings" className="text-primary underline">Settings → Profile</Link>.
                    </p>
                  )}
                </div>

                {/* Who gets tagged. Says what will really happen for the selected gig. */}
                {(() => {
                  const gig = gigs.find((g) => g.gig_id === selectedGig);
                  const people = gig ? summariseMusicianTags(gig, collaborators) : null;
                  let musiciansHelp = "Tags the musicians on the gig who have a handle saved in Scene";
                  if (people) {
                    if (people.total === 0) {
                      musiciansHelp = "No musicians on this gig yet. Add them in the gig form.";
                    } else if (people.tagged === 0) {
                      musiciansHelp = `None of the ${people.total} musicians on this gig have a handle saved yet. Add them in Scene.`;
                    } else {
                      musiciansHelp = `Will tag ${people.tagged} of ${people.total} musicians on this gig`
                        + (people.missing.length ? `. No handle saved for ${people.missing.join(", ")}.` : ".");
                    }
                  }
                  const venueHandles = venueHandleList(venueRecord);
                  let venueHelp = "Tags the venue when it has a handle saved in Scene";
                  if (gig) {
                    if (!gig.venue_name) venueHelp = "This gig has no venue yet.";
                    else if (venueHandles.length) venueHelp = `Will tag ${gig.venue_name}: ${venueHandles.map((h) => h.handle).join(", ")}`;
                    else venueHelp = `No social handle saved for ${gig.venue_name}. Add one in Scene to tag it.`;
                  }
                  return (
                    <>
                      <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Users className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <Label htmlFor="include-collabs" className="cursor-pointer">Tag musicians on this gig</Label>
                            <p className="text-xs text-muted-foreground" data-testid="tag-musicians-help">{musiciansHelp}</p>
                          </div>
                        </div>
                        <Switch id="include-collabs" checked={includeCollaborators} onCheckedChange={setIncludeCollaborators} />
                      </div>
                      <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Building2 className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <Label htmlFor="include-venue" className="cursor-pointer">Tag the venue</Label>
                            <p className="text-xs text-muted-foreground" data-testid="tag-venue-help">{venueHelp}</p>
                          </div>
                        </div>
                        <Switch id="include-venue" checked={includeVenue} onCheckedChange={setIncludeVenue} />
                      </div>
                    </>
                  );
                })()}

                {collaborators.length === 0 && (
                  <Link to="/collaborators">
                    <Button variant="outline" size="sm" className="w-full">
                      <Users className="h-4 w-4 mr-2" />
                      Add Collaborators
                    </Button>
                  </Link>
                )}

                {/* Generate Button */}
                <Button
                  onClick={handleGenerate}
                  disabled={!selectedGig || generating}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 btn-glow"
                  data-testid="generate-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating with AI...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate Posts
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Image Search removed — frontend-only deletion per spec.
                Backend endpoint /api/images/search remains untouched and
                continues to be available for any other flow. */}
          </div>

          {/* Generated Posts */}
          <div>
            {generatedPosts ? (
              <Card
                className="glass-panel"
                style={{ background: tokens.color.cardBg, border: tokens.border.card, borderRadius: tokens.radius.lg }}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-primary" />
                      Generated Posts
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleGenerate}
                      disabled={generating}
                      data-testid="regenerate-btn"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
                      Regenerate
                    </Button>
                  </div>
                  {selectedGigData && (
                    <CardDescription>
                      For: {selectedGigData.artist_name} @ {selectedGigData.venue_name}
                    </CardDescription>
                  )}
                  {generatedPosts.collaborators_included > 0 && (
                    <Badge variant="outline" className="w-fit mt-2 text-xs">
                      <Users className="h-3 w-3 mr-1" />
                      {generatedPosts.collaborators_included} collaborator(s) tagged
                    </Badge>
                  )}
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue={platforms[0]} className="w-full">
                    <TabsList className="grid w-full grid-cols-3 bg-secondary">
                      {platforms.map(platform => (
                        <TabsTrigger 
                          key={platform} 
                          value={platform}
                          className="data-[state=active]:bg-card"
                        >
                          {getPlatformIcon(platform)}
                          <span className="ml-2 hidden sm:inline">{getPlatformName(platform)}</span>
                        </TabsTrigger>
                      ))}
                    </TabsList>

                    {platforms.map(platform => (
                      <TabsContent key={platform} value={platform} className="mt-4">
                        {generatedPosts.posts[platform] ? (
                          <div className="space-y-4">
                            {/* Selected Image Preview */}
                            {selectedImage && (
                              <div className="relative aspect-video rounded-lg overflow-hidden">
                                <img 
                                  src={selectedImage.url} 
                                  alt="Selected"
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            )}

                            {/* Post Text */}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <Label>Post Text</Label>
                                <div className="flex gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCopy(platform, generatedPosts.posts[platform].text)}
                                    data-testid={`copy-${platform}`}
                                  >
                                    {copiedPlatform === platform ? (
                                      <>
                                        <Check className="h-4 w-4 mr-1 text-emerald-500" />
                                        Copied!
                                      </>
                                    ) : (
                                      <>
                                        <Copy className="h-4 w-4 mr-1" />
                                        Copy
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </div>
                              <Textarea
                                value={generatedPosts.posts[platform].text}
                                onChange={(e) => {
                                  setGeneratedPosts(prev => ({
                                    ...prev,
                                    posts: {
                                      ...prev.posts,
                                      [platform]: { ...prev.posts[platform], text: e.target.value }
                                    }
                                  }));
                                }}
                                className="min-h-[200px] bg-secondary/50 text-sm"
                                data-testid={`post-text-${platform}`}
                              />
                            </div>

                            {/* Copy & Open Buttons */}
                            <div className="flex flex-col gap-2 pt-3 border-t border-border">
                              <p className="text-xs font-medium text-muted-foreground">Quick Share</p>
                              <div className="grid grid-cols-2 gap-2">
                                <Button
                                  variant="outline"
                                  className="justify-start"
                                  onClick={() => {
                                    handleCopy(platform, generatedPosts.posts[platform].text);
                                  }}
                                  data-testid={`copy-only-${platform}`}
                                >
                                  <Copy className="h-4 w-4 mr-2" />
                                  Copy Caption
                                </Button>
                                {/* Hide "Copy & Open" for Instagram on desktop */}
                                {(platform !== 'instagram' || isMobile) && (
                                  <Button
                                    className="justify-start"
                                    onClick={async () => {
                                      await handleCopy(platform, generatedPosts.posts[platform].text);
                                      
                                      const text = generatedPosts.posts[platform].text;
                                      
                                      let url;
                                      if (platform === 'instagram') {
                                        url = 'instagram://app';
                                      } else if (platform === 'facebook') {
                                        url = isMobile ? 'fb://feed' : 'https://www.facebook.com/';
                                      } else if (platform === 'twitter') {
                                        const encodedText = encodeURIComponent(text.substring(0, 280));
                                        url = isMobile 
                                          ? `twitter://post?text=${encodedText}`
                                          : `https://x.com/intent/tweet?text=${encodedText}`;
                                      }
                                      
                                      if (isMobile) {
                                        window.location.href = url;
                                      } else {
                                        window.open(url, '_blank', 'noopener,noreferrer');
                                      }
                                    }}
                                    data-testid={`copy-open-${platform}`}
                                  >
                                    {getPlatformIcon(platform)}
                                    <span className="ml-2">Copy & Open</span>
                                    <ExternalLink className="h-4 w-4 ml-auto" />
                                  </Button>
                                )}
                                {/* Show message for Instagram on desktop */}
                                {platform === 'instagram' && !isMobile && (
                                  <div className="flex items-center justify-center text-xs text-muted-foreground bg-secondary/50 rounded-md px-3">
                                    Open Instagram on mobile to paste
                                  </div>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {platform === 'twitter' 
                                  ? 'Opens X with text pre-filled' 
                                  : platform === 'instagram' && !isMobile
                                    ? 'Instagram posting only works on mobile app'
                                    : `Caption copied - paste in ${getPlatformName(platform)}`}
                              </p>
                            </div>

                            {/* Suggestions */}
                            {generatedPosts.posts[platform].image_suggestion && (
                              <div className="p-4 bg-secondary/30 rounded-lg">
                                <p className="text-xs font-medium text-muted-foreground mb-1">Image Suggestion</p>
                                <p className="text-sm">{generatedPosts.posts[platform].image_suggestion}</p>
                              </div>
                            )}

                            {generatedPosts.posts[platform].best_time && (
                              <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg">
                                <span className="text-xs text-muted-foreground">Best posting time:</span>
                                <Badge variant="outline" className="text-primary border-primary/30">
                                  {generatedPosts.posts[platform].best_time}
                                </Badge>
                              </div>
                            )}

                            {/* Hashtag Suggestions */}
                            {(suggestedHashtags[platform]?.length > 0 || loadingHashtags) && (
                              <div className="space-y-2 pt-2 border-t border-border" data-testid={`hashtags-${platform}`}>
                                <div className="flex items-center gap-2">
                                  <Hash className="h-4 w-4 text-primary" />
                                  <span className="text-xs font-medium text-muted-foreground">Suggested Hashtags</span>
                                </div>
                                {loadingHashtags ? (
                                  <div className="flex items-center gap-2 py-2">
                                    <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                                    <span className="text-xs text-muted-foreground">Loading hashtags...</span>
                                  </div>
                                ) : (
                                  <div className="flex flex-wrap gap-1.5">
                                    {suggestedHashtags[platform]?.map(tag => {
                                      const isAdded = generatedPosts.posts[platform]?.text?.includes(tag);
                                      return (
                                        <button
                                          key={tag}
                                          onClick={() => addHashtagToPost(platform, tag)}
                                          disabled={isAdded}
                                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                                            isAdded 
                                              ? 'bg-primary/20 text-primary cursor-default' 
                                              : 'bg-secondary/60 text-muted-foreground hover:bg-primary/20 hover:text-primary cursor-pointer'
                                          }`}
                                          data-testid={`hashtag-chip-${tag.replace('#', '')}`}
                                        >
                                          {tag}
                                          {isAdded ? (
                                            <Check className="h-3 w-3" />
                                          ) : (
                                            <Plus className="h-3 w-3" />
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-muted-foreground text-center py-8">
                            No post generated for {platform}
                          </p>
                        )}
                      </TabsContent>
                    ))}
                  </Tabs>
                </CardContent>
              </Card>
            ) : (
              <Card
                className="h-full min-h-[400px] flex items-center justify-center"
                style={{ background: tokens.color.cardBg, border: tokens.border.card, borderRadius: tokens.radius.lg }}
              >
                <CardContent className="text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg mb-2">AI-Powered Posts</h3>
                  <p className="text-muted-foreground text-sm max-w-xs mx-auto mb-4">
                    Select a gig and click generate to create rich promotional posts with specific details about your show.
                  </p>
                  {collaborators.length > 0 && (
                    <Badge variant="outline" className="text-xs">
                      <Users className="h-3 w-3 mr-1" />
                      {collaborators.length} collaborator(s) will be tagged
                    </Badge>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SocialGenerator;
